/**
 * Layout wrapper component.
 * Main wrapper that selects layout based on settings.
 * Based on Vuexy LayoutWrapper component, adapted for Vite React.
 */

// React Imports
import React, { type ReactElement, type ReactNode } from 'react';

// Type Imports
import type { SystemMode } from '@core/types';

// Hook Imports
import { useSettings } from '@core/hooks/useSettings';
import useLayoutInit from '@core/hooks/useLayoutInit';

type LayoutWrapperProps = {
  systemMode: SystemMode;
  verticalLayout: ReactElement;
  horizontalLayout?: ReactElement;
  children?: ReactNode;
};

/**
 * Layout wrapper component
 */
const LayoutWrapper = (props: LayoutWrapperProps) => {
  // Props
  const { systemMode, verticalLayout, horizontalLayout, children } = props;

  // Hooks
  const { settings } = useSettings();

  useLayoutInit(systemMode);

  // Clone the layout element and inject children
  const layoutWithChildren = React.cloneElement(
    settings.layout === 'horizontal' && horizontalLayout ? horizontalLayout : verticalLayout,
    {},
    children
  );

  // Return the layout based on the layout context
  return (
    <div className="flex flex-col flex-auto" data-skin={settings.skin}>
      {layoutWithChildren}
    </div>
  );
};

export default LayoutWrapper;
